import { FC, useEffect, useState } from "react";
import { ModalProposta, Projeto } from "../../types";
import api, { apiNoLoading, IDataRequest, IDataResponse, STATUS_CODE } from "src/framework/providers/api";
import { ModalPropostas } from "../../ModalPropostas";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import { useObservable } from "src/framework/store/storeUtil";
import { StringUtil } from "src/framework/commons/StringUtil";
import { LoginStore } from "src/framework/store/LoginStore";
import { confirmationDialog } from "src/framework/store/ConfirmationDialogStore";
import { notifierStore } from "src/framework/store/NotifierStore";

export enum TiposVisualizacaoProposta {
    SIMPLIFICADA = "simplificada",
    DETALHADA = "detalhada"
}

export const TIPO_PROPOSTA = {
    "simplificada": "proposta_simplificada",
    "detalhada": "proposta"
}

interface ModalVisualizarPropostasParams {
    open: boolean;
    setOpenModal: Function;
    tipo?: TiposVisualizacaoProposta;
    projeto?: Projeto;
}

const ModalVisualizarProposta: FC<ModalVisualizarPropostasParams> = (modalVisualizarPropostasParams) => {
    const [dataModalProposta, setDataModalProposta] = useState<ModalProposta | null>(null);
    const [openModalProposta, setOpenModalProposta] = useState<boolean>(false);
    const permissoes:string[] = useObservable(LoginStore.permissoes) || [];
    
    const handleClose = () => {
        modalVisualizarPropostasParams.setOpenModal(false);
    };
    
    useEffect(() => {
        const projetoAtual = modalVisualizarPropostasParams.projeto;
        if (projetoAtual !== undefined) {
            processaChamadaPdf(projetoAtual, permissoes)
        }
    }, [modalVisualizarPropostasParams.open, permissoes]);

    const gerarProposta = async (url: string, codigoVenda: string, valores: boolean, tipo: TiposVisualizacaoProposta) => {
        const dataRequest = {
            url: url,
            link: url,
            tipo: TIPO_PROPOSTA[tipo],
            valores: valores ?? false,
        }
        const request: IDataRequest = {
            url: `/projetos/uploads/gerar/${codigoVenda}`,
            data: dataRequest
        }
        return await apiNoLoading.post(request);
    }

    const abrirNovaAba = (codigoVenda: string, valores: boolean, tipo: TiposVisualizacaoProposta) => {
        handleClose();
        const url = `/propostas/${tipo === "simplificada" ? "simplificada" : "detalhada"}/${StringUtil.encode64(codigoVenda, valores, tipo)}`
        const urlCompleta = `${window.location.origin}${url}`;
        gerarProposta(urlCompleta, codigoVenda, valores, tipo);
        window.open(urlCompleta, '_blank', 'noopener,noreferrer');
    };

    async function processaChamadaPdf(projetoAtual: Projeto, permissoesList: typeof permissoes) {
        let condition = 0;
        if (modalVisualizarPropostasParams.tipo !== undefined && permissoesList.length) {
            const aviso = `Não foi possível carregar a proposta ${modalVisualizarPropostasParams.tipo} do projeto.`
            const codigoVenda = modalVisualizarPropostasParams?.projeto?.codigo_venda || "";
            if (PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_VISUALIZAR_PROPOSTA_DISCRIMINAR_VALORES, permissoesList) && projetoAtual?.tipo !== "vertical") {
                confirmationDialog.setup({
                    title: "",
                    content: 'Deseja discriminar valores unitários na proposta?',
                    onClose: () => {
                        condition +=1
                        confirmationDialog.close();
                        if (condition < 2) {
                            if(projetoAtual?.segmento?.includes("vertical") && projetoAtual.tipo === "vertical") {
                                const response = callPdf(projetoAtual, "false", modalVisualizarPropostasParams.tipo+"")
                                showPdf(response, projetoAtual, modalVisualizarPropostasParams.tipo+"", aviso)
                            } else {
                                abrirNovaAba(codigoVenda, false, modalVisualizarPropostasParams?.tipo ? modalVisualizarPropostasParams?.tipo : TiposVisualizacaoProposta.SIMPLIFICADA);
                            }
                        }
                    },
                    onConfirmation: async () => {
                        condition +=1
                        if(projetoAtual?.segmento?.includes("vertical") && projetoAtual.tipo === "vertical") {
                            const response = callPdf(projetoAtual, "true", modalVisualizarPropostasParams.tipo+"")
                            showPdf(response, projetoAtual, modalVisualizarPropostasParams.tipo+"", aviso)
                        } else {
                            abrirNovaAba(codigoVenda, true, modalVisualizarPropostasParams?.tipo ? modalVisualizarPropostasParams?.tipo : TiposVisualizacaoProposta.SIMPLIFICADA);
                        }
                    }
                })
                confirmationDialog.open()
            } else {
                if(projetoAtual?.segmento?.includes("vertical") && projetoAtual.tipo === "vertical") {
                    const response = callPdf(projetoAtual, "false", modalVisualizarPropostasParams.tipo+"")
                    showPdf(response, projetoAtual, modalVisualizarPropostasParams.tipo+"", aviso)
                } else {
                    abrirNovaAba(codigoVenda, false, modalVisualizarPropostasParams?.tipo ? modalVisualizarPropostasParams?.tipo : TiposVisualizacaoProposta.SIMPLIFICADA);
                }
            }
        }
    }

    async function callPdf(projeto: Projeto, svalores?: string, tipo?: string) : Promise<IDataResponse<any>> {
        setDataModalProposta(null);
        let url = ''
        if(svalores && tipo) {
            url = `/projetos/propostas/pdf?codigo_venda=${projeto.codigo_venda}&svalores=${svalores}&tipo=${tipo}&forca_atualizacao_proposta=true`
        } 
        if (!svalores && tipo) {
            url = `/projetos/propostas/pdf?codigo_venda=${projeto.codigo_venda}&tipo=${tipo}&forca_atualizacao_proposta=true`
        }
        if (projeto?.migrado || projeto?.origem === 'V1') {
            const propostaV2 = projeto?.uploads?.every(i => i?.origem === 'V2');
            if (propostaV2) {
                url = `/projetos/propostas/pdf?codigo_venda=${projeto.codigo_venda}&svalores=${svalores}&tipo=${tipo}&forca_atualizacao_proposta=true`
            } else {
                url = `/projetos/propostas/pdf?codigo_venda=${projeto.codigo_venda}&tipo=${tipo}`
            }
        }
        const request: IDataRequest = {
            url: url,
        }
        return await api.getFile(request)
    } 


    async function showPdf(response: Promise<IDataResponse<any>>, projeto: Projeto, tipo: string, aviso: string) {
        response.then(r => {
            const blob = new Blob([new Uint8Array(r.data)],{type:'application/pdf'})
            if (r.statusCode === STATUS_CODE.OK && blob) {
                setDataModalProposta({
                    codigo_venda: projeto?.codigo_venda ||  "",
                    projetoId: projeto?.id || 0,
                    url: URL.createObjectURL(blob),
                    tipo: tipo,
                });
                setOpenModalProposta(true);
            } else {
                mostraAvisoPropostaSemPdf(aviso);
            }
        }).catch((e) => {
            mostraAvisoPropostaSemPdf(aviso);
        });
    } 

    function mostraAvisoPropostaSemPdf(texto: string) {
        notifierStore.setNotifier({
            title: "Aviso!!!",
            content: [texto],
            timeOut: 3500,
            onClose: () => {
                notifierStore.setOpen(false);
                handleClose();
            }
        });
        notifierStore.setOpen(true); 
    }

    return (
        <>
        { dataModalProposta && (
            <ModalPropostas
            codigo_venda={dataModalProposta.codigo_venda}
            projetoId={dataModalProposta.projetoId}
            open={openModalProposta}
            setOpen={handleClose}
            mostrarValores={dataModalProposta.valoresUnitarios}
            url={dataModalProposta.url}
            tipo={dataModalProposta.tipo}
            />
        )}
        </>
    )
}

export { ModalVisualizarProposta }
